<template>
    <BaseModal name="service-inquiry" :adaptive="true" :width="'100%'" :max-width="560" :height="'auto'"
               :scrollable="true"
               @before-open="beforeOpen">
        <div v-if="service" class="tw-mt-1 tw-mb-8">
            <div class="tw-inline-grid tw-grid-flow-col tw-items-center tw-gap-3">
                <div class="tw-w-13 tw-h-13">
                    <BaseArtwork class="tw-rounded tw-overflow-hidden"
                                 :src="service.image(300)"
                                 :image-size="300"/>
                </div>
                <div>
                    <div class="tw-font-bold tw-text-2xl tw-text-infinity-highlight">Get a quote</div>
                    <router-link
                        class="tw-inline-block tw-text-md hover:tw-underline tw-text-infinity-tertiary-text"
                        @click.native="$modal.hide('soundkit-pricing')"
                        :to="{name: 'service', params: {service: service.id}}">{{ service.name }}
                    </router-link>
                </div>
            </div>
        </div>
        <template v-if="inquiry && !hideSuccessAlert">
            <BaseAlert type="success">
                Your inquiry has been successfully created. We will get back to you as soon as possible.
            </BaseAlert>
            <div class="tw-mt-4 tw-flex tw-justify-end tw-space-x-3">
                <button type="submit" class="tw-w-24 c-button c-button--default" @click="$modal.hide('service-inquiry')">
                    <span>Ok</span>
                </button>
            </div>
        </template>
        <template v-else>
            <form novalidate @submit="onSubmit">
                <div class="tw-grid tw-gap-3 sm:tw-grid-cols-2">
                    <BaseControl v-model="model.artistName" :trigger-validation="triggerValidation"
                                 :constraints="constraints.artistName"
                                 @validated="validated.artistName = $event" key="artistName"
                                 class="sm:tw-col-span-2"
                                 type="text"
                                 :disabled="inquiry"
                                 label="Artist Name"/>
                    <BaseControl v-model="model.artistEmail" :trigger-validation="triggerValidation"
                                 :disabled="inquiry"
                                 :constraints="constraints.artistEmail" @validated="validated.artistEmail = $event" key="artistEmail" type="text" label="Artist Email"/>
                    <BaseControl v-model="model.artistPhone" :trigger-validation="triggerValidation"
                                 :disabled="inquiry"
                                 :constraints="constraints.artistPhone" @validated="validated.artistPhone = $event" key="artistPhone" type="text" label="Artist Phone"/>
                    <BaseControl v-model="model.projectTitle" :trigger-validation="triggerValidation"
                                 :disabled="inquiry"
                                 :constraints="constraints.projectTitle" @validated="validated.projectTitle = $event" key="projectTitle" type="text"
                                 label="Project Title"/>
                    <BaseControl v-model="model.projectBudget" :trigger-validation="triggerValidation"
                                 :disabled="inquiry"
                                 right-icon="dollar-sign"
                                 type="number"
                                 :constraints="constraints.projectBudget" @validated="validated.projectBudget = $event" key="projectBudget"
                                 label="Project Budget"/>
                    <BaseControl v-model="model.projectDescription" :trigger-validation="triggerValidation"
                                 :disabled="inquiry"
                                 :constraints="constraints.projectDescription" @validated="validated.projectDescription = $event" key="projectDescription"
                                 class="sm:tw-col-span-2" type="text" label="Project Description"/>
                </div>
                <div class="tw-text-infinity-error tw-p-3 tw-rounded tw-border tw-border-solid tw-border-infinity-error tw-my-3" v-if="error">
                    An error occurred please try other time.
                </div>
                <div class="tw-mt-8 tw-flex tw-justify-end tw-space-x-3">
                    <button type="button" class="c-button c-button--neutral" @click="$modal.hide('service-inquiry')">
                        Cancel
                    </button>
                    <button type="submit" class="c-button c-button--default" :disabled="loading">
                        <font-awesome-icon icon="spinner" class="tw-mr-2" spin v-if="loading" />
                        <span>Send Inquiry</span>
                    </button>
                </div>
            </form>
        </template>
    </BaseModal>

</template>
<script>
import BaseArtwork from "../base/base-artwork";
import BaseModal from "../base/base-modal";
import BaseControl from "../base/base-control";
import BaseAlert from "../base/base-alert";

export default {
    components: {BaseModal, BaseArtwork, BaseControl, BaseAlert},
    data() {
        return {
            service: null,
            inquiry: null,
            loading: false,
            error: null,
            triggerValidation: false,
            success: false,
            hideSuccessAlert: false,
            validated: {
                artistName: false,
                artistEmail: false,
                artistPhone: false,
                projectTitle: false,
                projectBudget: false,
                projectDescription: false,
            },
            model: {
                artistName: '',
                artistEmail: '',
                artistPhone: '',
                projectTitle: '',
                projectBudget: '',
                projectDescription: '',
            },
            constraints: {
                artistName: {
                    presence: {
                        allowEmpty: false,
                        message: 'Please enter the artist name'
                    }
                },
                artistEmail: {
                    presence: {
                        allowEmpty: false,
                        message: 'Please enter the artist email'
                    }
                },
                artistPhone: {
                    presence: {
                        allowEmpty: false,
                        message: 'Please enter the artist phone'
                    }
                },
                projectTitle: {
                    presence: {
                        allowEmpty: false,
                        message: 'Please enter the project title'
                    }
                },
                projectBudget: {
                    presence: {
                        allowEmpty: false,
                        message: 'Please enter the project budget'
                    }
                },
                projectDescription: {
                    presence: {
                        allowEmpty: false,
                        message: 'Please enter the project description'
                    }
                }
            },
        };
    },
    methods: {
        beforeOpen(event) {
            this.triggerValidation = false;
            this.inquiry = null;
            this.error = null;
            this.model = {
                artistName: '',
                artistEmail: '',
                artistPhone: '',
                projectTitle: '',
                projectBudget: '',
                projectDescription: '',
            };
            this.validated = {
                artistName: false,
                artistEmail: false,
                artistPhone: false,
                projectTitle: false,
                projectBudget: false,
                projectDescription: false,
            };
            this.service = event.params.service;
        },
        async onSubmit(event) {
           try {
               event.preventDefault();
               this.triggerValidation = true;
               const allValidated = Object.values(this.validated).every(v => v);
               if (!allValidated) {
                   return;
               }
               this.loading = true;
               this.error = null;
               this.success = false;
               const response = await this.$http.post(`${ab.apiUrl}/infinity/services/inquiry`, {
                   'user_id': ab.user.id,
                   'service_id': this.service.id,
                   'artist_name': this.model.artistName,
                   'artist_email': this.model.artistEmail,
                   'artist_phone': this.model.artistPhone,
                   'project_title': this.model.projectTitle,
                   'project_budget': this.model.projectBudget,
                   'project_description': this.model.projectDescription,
               });
               this.inquiry = response.data;
           } catch (e) {
                this.error = e
                console.error(e)
           } finally {
               this.loading = false;
           }
        },
    }
}
</script>
